import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { MatDialog } from '@angular/material/dialog';
import { DatePipe } from '@angular/common';
import { NgSelectComponent } from '@ng-select/ng-select';
import { finalize, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, forkJoin, of, ReplaySubject } from 'rxjs';
import { tap } from 'rxjs/internal/operators/tap';
import { get } from 'lodash';

import { ToasterService } from 'common/src/modules/ui-components/toaster';
import { BaseModalComponent } from 'common/src/modules/rnpl-common/components';
import { BundledProduct, ProductModel, ProductsService, RelatedProductModel } from '../../../products';
import { ProductTypes } from '../../../products/product-types';
import { InvoiceApiService } from 'projects/workspace/src/app/outgoing-invoice/services/invoice-api.service';
import {
  OutgoingInvoiceModel,
  OutgoingInvoicePositionModel,
  PositionAccountingSettings
} from 'projects/workspace/src/app/outgoing-invoice/models';
import { CompositeProductModalComponent } from '../../modals-products';
import { TimeTrackingApiService } from 'projects/workspace/src/app/time-tracking/services/time-tracking-api.service';
import { TimeTrackingRecordModel } from 'projects/workspace/src/app/time-tracking/models';
import { TimeTrackingListTabsEnum } from 'projects/workspace/src/app/time-tracking/enums';
import { CustomSearchFn, getProductSearchValue } from 'common/src/modules/rnpl-common/helpers';
import { selectCompanyProfile } from 'projects/workspace/src/app/administration/store/selectors';
import { CompanyProfile } from 'projects/workspace/src/app/administration/models/company-profile.model';
import { AppState } from 'projects/workspace/src/app/store/state/app.state';
import { PauseModalsHintsComponent } from '../../modals-subscription/pause-modals-hints/pause-modals-hints.component';
import { DocumentTypesUppercaseEnum } from '../../modals-common/link-document-modal/enums/ducument-types.enum';
import { RecalculatePositionApiService } from '../../../../services/recalculate-position-api.service';
import { ARBITRARY_POSITION_TYPES_LIST, CORRECTION_POSITION_TYPES_LIST } from 'projects/workspace/src/app/accounting/accounting.constants';
import { CommonModalsActionsEnum, WarningModalComponent } from '../../modals-common';
import { CHANGE_CORRECTION_CALCULATION_MODAL_DATA } from '../../modals.contsans';
import {
  PositionAccountingSettingsComponent
} from 'projects/workspace/src/app/shared/components/position-accounting-settings/position-accounting-settings.component';
import { OinTypeEnum, OutgoingInvoiceListTabsEnum } from 'projects/workspace/src/app/outgoing-invoice/enums';
import { ProductUnitModel } from 'projects/workspace/src/app/shared/models';
import { ProductUnitApiService } from 'projects/workspace/src/app/shared/services';
import { ReportingPeriodsPreferencesModel } from 'projects/workspace/src/app/reporting-periods/models';
import { selectReportingSettings } from 'projects/workspace/src/app/store/selectors/shared.selectors';
import { PositionGroupingEnum, PositionMediaTypeEnum, PriceOptionEnum } from 'projects/workspace/src/app/shared/enums';
import { PositionMediaFormComponent } from 'projects/workspace/src/app/shared/forms/position-media-form/position-media-form.component';
import { PdfSettingsAlignEnum } from 'projects/workspace/src/app/pdf-settings/enums';
import { CustomerTypeEnum } from '../../../../models';

@Component({
  selector: 'rnpl-outgoing-invoice-position-modal',
  templateUrl: './outgoing-invoice-position-modal.component.html',
  styleUrls: ['./outgoing-invoice-position-modal.component.scss']
})
export class OutgoingInvoicePositionModalComponent extends BaseModalComponent implements OnInit {

  public bundledProducts: Partial<BundledProduct>[];
  public activeTab: 'OVERVIEW'|'BUNDLE_PRODUCTS' = 'OVERVIEW';
  public tabs = [
    {
      tabName: 'OVERVIEW',
      label: 'APP.POSITION'
    },
    {
      tabName: 'BUNDLE_PRODUCTS',
      label: 'BUNDLES.PRODUCTS_IN_BUNDLE'
    }
  ];

  public correctionPositionTypesList = CORRECTION_POSITION_TYPES_LIST;
  public arbitraryPositionTypesList = ARBITRARY_POSITION_TYPES_LIST;
  public unitsList: string[] = [];
  public unitsListSource: ProductUnitModel[] = [];
  public companyProfile: CompanyProfile;
  public reportingPeriodPreferences: ReportingPeriodsPreferencesModel;

  public productTypes = ProductTypes;
  public positionMediaTypeEnum = PositionMediaTypeEnum;
  public positionGroupingEnum = PositionGroupingEnum;
  public selectedRecordInvalid: boolean = false;
  public selectedProductInvalid: boolean = false;
  public productNameInvalid: boolean = false;
  public previousUnitType: string;
  public previousUpdatedPosition: any;

  public productsList: ProductModel[] = [];
  public productsListAllTypes: ProductModel[] = [];
  public timeTrackingRecords: TimeTrackingRecordModel[] = [];
  public relatedProducts: RelatedProductModel[] = [];

  public productTypeRadio: 'common'|'general' = 'common';
  public currentStep: 'positionInfo'|'relatedProducts' = 'positionInfo';
  public isTimeTrackingProducts: boolean = false;
  public notFromBaseDocument: boolean = false;
  public accountingSettings: PositionAccountingSettings;
  public selectedProduct: ProductModel = null;
  public selectedRecord: TimeTrackingRecordModel = null;
  public positionName: string = null;
  public selectedProductType: ProductTypes = ProductTypes.GOODS;
  public positionFromTimeTracking: OutgoingInvoicePositionModel;

  public customSearchFn = CustomSearchFn;

  readonly generalProductsList$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

  readonly addPositionRequest$: BehaviorSubject<boolean> = new BehaviorSubject(null);
  readonly showDropdownSpin$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  readonly destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  public form: FormGroup;

  @ViewChild('select', {static: false}) public dropdown: NgSelectComponent;
  @ViewChild('hint', { static: false }) hint: PauseModalsHintsComponent;
  @ViewChild('positionAccountingSettings', { static: false }) positionAccountingSettingsRef: PositionAccountingSettingsComponent;
  @ViewChild('positionMediaFormComponent', { static: false }) positionMediaFormComponentRef: PositionMediaFormComponent;

  constructor(
    public store: Store<AppState>,
    public toasterService: ToasterService,
    public translateService: TranslateService,
    private productsService: ProductsService,
    private invoiceApiService: InvoiceApiService,
    private timeTrackingApiService: TimeTrackingApiService,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<OutgoingInvoicePositionModalComponent>,
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private recalculatePositionApiService: RecalculatePositionApiService,
    private productUnitApiService: ProductUnitApiService,
    @Inject(MAT_DIALOG_DATA) public data: {
      invoice: OutgoingInvoiceModel,
      groupId: string;
    }
  ) {
    super(toasterService);

    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });

    this.store.select(selectCompanyProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe((companyProfile: CompanyProfile) => {
        this.companyProfile = companyProfile;
      });

    this.store.select(selectReportingSettings)
      .pipe(takeUntil(this.destroy$))
      .subscribe((preferences: ReportingPeriodsPreferencesModel) => {
        this.reportingPeriodPreferences = preferences;
      });
  }

  ngOnInit() {
    this.initForm(this.selectedProduct);
    this.productTypeControl.setValue(ProductTypes.GOODS);
    this.getProducts();
    // this.getGeneralProductsListDropdown();

    if (
      !this.hasLinkedSO &&
      !this.hasLinkedNotTechnicalSO &&
      !this.hasLinkedOFR &&
      !this.hasLinkedSBC &&
      !this.hasLinkedECO
    ) {
      this.notFromBaseDocument = true;
    }

    if (
      (this.companyProfile.subscriptionManagement.channelOptions.servicesEnabled && this.companyProfile.productOptions.servicesEnabled)
      || this.companyProfile.subscriptionManagement.extensionsActive.timeTracking
    ) {
      if (this.data.invoice.customerId && !this.hasLinkedSO) {
        const filters: any = {
          billable: true,
          loadImages: true,
          approved: true,
          hasDocuments: false,
          partnerIds: [this.data.invoice.customerId]
        };

        this.timeTrackingApiService.getRecordsListDetails(TimeTrackingListTabsEnum.OPEN, filters)
          .pipe(takeUntil(this._destroy))
          .subscribe(records => {
            this.timeTrackingRecords = records;
          });
      }
    }
  }

  private initForm(selectProduct: ProductModel = {} as ProductModel, fromDocument: boolean = false): void {
    this.form = this.fb.group({
      grossTotalPrice: [
        fromDocument
          ? this.setCurrencyValue(get(selectProduct, 'grossTotalPrice'))
          : this.setCurrencyValue(get(selectProduct, 'prices.price.grossPrice')),
        Validators.required
      ],
      grossUnitPrice: [
        fromDocument
          ? this.setCurrencyValue(get(selectProduct, 'grossUnitPrice'))
          : this.setCurrencyValue(get(selectProduct, 'prices.price.grossPrice')),
        Validators.required
      ],
      netTotalPrice: [
        fromDocument
          ? this.setCurrencyValue(get(selectProduct, 'netTotalPrice'))
          : this.setCurrencyValue(get(selectProduct, 'prices.price.netPrice')),
        Validators.required
      ],
      productDescription: [get(selectProduct, 'description')],
      providedServices: [ get(selectProduct, 'description')],
      description: [
        fromDocument
          ? get(selectProduct, 'name')
          : get(selectProduct, 'description')
      ],
      netUnitPrice: [
        fromDocument
          ? this.setCurrencyValue(get(selectProduct, 'netUnitPrice'))
          : this.setCurrencyValue(get(selectProduct, 'prices.price.netPrice')),
        Validators.required
      ],
      vat: [
        fromDocument
          ? get(selectProduct, 'vat')
          : get(selectProduct, 'prices.price.vat') || get(selectProduct, 'prices.price.vat') === 0
            ? get(selectProduct, 'prices.price.vat')
            : get(selectProduct, 'accountingSettings.vatRateByDefault')
      ],
      discount: [
        fromDocument
          ? get(selectProduct, 'discount')
          : get(selectProduct, 'prices.price.discount'),
      ],
      discountAmountNet: [
        fromDocument
          ? get(selectProduct, 'discountAmountNet')
          : this.setCurrencyValue(get(selectProduct, 'prices.price.discountNet'))
      ],
      discountAmountGross: [
        fromDocument
          ? get(selectProduct, 'discountAmountGross')
          : this.setCurrencyValue(get(selectProduct, 'prices.price.discountGross'))
      ],
      addDiscount: [
        fromDocument
          ? !!get(selectProduct, 'discount')
          : !!get(selectProduct, 'prices.price.discount')
      ],
      quantity: [fromDocument ? get(selectProduct, 'available') : 1],
      delivered: [],
      providedFrom: [get(selectProduct, 'providedFrom')],
      providedTo: [get(selectProduct, 'providedTo')],
      remark: [],
      addRemark: [false],
      productType: [
        fromDocument
          ? get(selectProduct, 'arbitraryProductType') || get(selectProduct, 'type')
          : null
      ],
      unitType: [get(selectProduct, 'unitType', 'pcs')],
      productDescriptionUpdated: [false],
      correctionType: this.isCorrectionProductType ? this.correctionType.value : null,
      specifyAmountsSeparately: [],
      vatTotal: [],
    });
  }

  public getPositionFromRecordProto(selectedRecord: TimeTrackingRecordModel): void {
    this.invoiceApiService.getOutgoingInvoicePositionFromRecordProto(this.data.invoice.invoiceId, this.selectedRecord.id)
      .pipe(
        finalize(() => this.addPositionRequest$.next(false)),
        takeUntil(this._destroy))
      .subscribe((response: OutgoingInvoicePositionModel) => {

        this.positionFromTimeTracking = response;

        this.selectedProductType = ProductTypes.SERVICES;
        this.selectedProduct = {
          ...response,
          productImgUrl: selectedRecord.product.imageUrl
        } as any;
        this.form.patchValue({
          ...response,
          description: response.providedServices,
        });
        this.productTypeControl.setValue(ProductTypes.SERVICES);
      });
  }

  selectPositionChangesTimeTracking(selectedRecord: TimeTrackingRecordModel) {
    if (this.vatBlocked) {
      this.vat.disable({onlySelf: true, emitEvent: false});
      if (this.priceOptionNet) {
        this.grossUnitPrice.disable({onlySelf: true, emitEvent: false});
        this.grossTotalPrice.disable({onlySelf: true, emitEvent: false});
      }
    }
    this.getPositionFromRecordProto(selectedRecord);
  }

  selectPositionChanges(selectedProduct: ProductModel): void {
    if (this.positionMediaFormComponentRef) {
      this.positionMediaFormComponentRef.setMediaType(PositionMediaTypeEnum.REGULAR);
    }

    this.selectedProductType = this.isTimeTrackingProducts
      ? ProductTypes.SERVICES
      : (selectedProduct.type || selectedProduct.productType);
    if (this.productTypeRadio === 'general') {
      this.selectedProductType = ProductTypes.GENERAL;
    }
    this.accountingSettings = null;
    if (this.positionAccountingSettingsRef) {
      this.positionAccountingSettingsRef.resetForm();
    }

    this.vat.enable({onlySelf: true, emitEvent: false});
    this.netTotalPrice.enable({onlySelf: true, emitEvent: false});
    this.netUnitPrice.enable({onlySelf: true, emitEvent: false});
    this.grossTotalPrice.enable({onlySelf: true, emitEvent: false});
    this.grossUnitPrice.enable({onlySelf: true, emitEvent: false});

    if ((this.hasLinkedSO || this.hasLinkedOFR || this.hasLinkedSBC) && !this.notFromBaseDocument) {
      if (this.selectedProduct.mediaType) {
        this.positionMediaFormComponentRef.setMediaType(this.selectedProduct.mediaType);
        if (this.selectedProduct.mediaType === PositionMediaTypeEnum.TEXT) {
          this.selectedProduct = {
            ...this.selectedProduct,
            description: this.selectedProduct.name,
            name: this.translateService.instant('ECOMMERCE.TEXT'),
            icon: 'user-defined'
          } as any;
        }
        if (this.selectedProduct.mediaType === PositionMediaTypeEnum.IMAGE) {
          this.selectedProduct = {
            ...this.selectedProduct,
            name: this.translateService.instant('FORM.IMAGE'),
            icon: 'image'
          } as any;
        }
        return;
      }
      this.initForm(this.selectedProduct, true);
      this.positionName = this.selectedProduct.name;
      this.accountingSettings = { ...this.selectedProduct.accountingSettings };
      this.accountingSettings.productTypeId = get(this.selectedProduct, 'accountingSettings.glProductTypeId');

      if (this.isPrepaymentProductType) {
        this.vat.setValue(selectedProduct.vatRate);

        if (this.isPrepaymentInvoice || this.isRegularInvoice) {
          this.vat.disable({onlySelf: true, emitEvent: false});
        }

        if (this.isRegularInvoice) {
          this.netTotalPrice.setValue(selectedProduct.amountNet);
          this.netUnitPrice.setValue(selectedProduct.amountNet);
          this.grossTotalPrice.setValue(selectedProduct.amountGross);
          this.grossUnitPrice.setValue(selectedProduct.amountGross);
          this.netTotalPrice.disable({onlySelf: true, emitEvent: false});
          this.netUnitPrice.disable({onlySelf: true, emitEvent: false});
          this.grossTotalPrice.disable({onlySelf: true, emitEvent: false});
          this.grossUnitPrice.disable({onlySelf: true, emitEvent: false});
        }
      }

      if (this.vatBlocked) {
        this.vat.setValue(0);
        this.vat.disable({onlySelf: true, emitEvent: false});
        this.vatTotal.disable({onlySelf: true, emitEvent: false});
        if (!this.isPrepaymentProductType) {
          this.grossTotalPrice.setValue(this.setCurrencyValue(get(this.selectedProduct, 'netTotalPrice')));
          this.grossUnitPrice.setValue(this.setCurrencyValue(get(this.selectedProduct, 'netTotalPrice')));
        }
        if (this.priceOptionNet) {
          this.grossUnitPrice.disable({onlySelf: true, emitEvent: false});
          this.grossTotalPrice.disable({onlySelf: true, emitEvent: false});
        }
      }

      // init position predefined data
      this.updatePosition(null, 'resolveRevenueGL');

    } else {
      this.getProductById(+selectedProduct.id);
    }

    this.getProductUnits();
    this.productTypeControl.setValue(selectedProduct['arbitraryProductType'] || selectedProduct.type);
  }

  selectVariation(selectedVariation: ProductModel): void {
    this.selectedProduct = this.productsListAllTypes.find(itm => +itm.id === +selectedVariation.productId);
    this.selectPositionChanges(this.selectedProduct);
  }

  public getProductUnits(): void {
    const productType = this.selectedProduct.type
      || this.selectedProduct.productType
      || this.selectedProduct.entityForm
      || this.productTypeControl.value;
    this.productUnitApiService.getUnits$(productType)
      .pipe(takeUntil(this.destroy$))
      .subscribe((unitsList: ProductUnitModel[]) => {
        this.unitsListSource = unitsList;
        this.unitsList = unitsList.map(u => u.name);
      });
  }

  public getProductById(productId) {
    this.productsService.getProduct(productId, this.data.invoice.salesPriceList)
      .pipe(takeUntil(this._destroy))
      .subscribe(product => {
        this.initForm(product);
        this.setBundledProducts(product);
        this.accountingSettings = { ...product.accountingSettings };
        this.accountingSettings.productTypeId = product.accountingSettings.glProductTypeId;
        this.productTypeControl.setValue(product.productType);

        if (this.vatBlocked) {
          this.vat.setValue(0);
          this.vat.disable({onlySelf: true, emitEvent: false});
          this.vatTotal.disable({onlySelf: true, emitEvent: false});
          this.grossTotalPrice.setValue(this.setCurrencyValue(get(product, 'prices.price.netPrice')));
          this.grossUnitPrice.setValue(this.setCurrencyValue(get(product, 'prices.price.netPrice')));
          if (this.priceOptionNet) {
            this.grossUnitPrice.disable({onlySelf: true, emitEvent: false});
            this.grossTotalPrice.disable({onlySelf: true, emitEvent: false});
          }
        }

        // if (product.productType === ProductTypes.BUNDLE && get(product, 'prices.price.priceFromBundledProducts')) {
        //   this.netTotalPrice.disable({onlySelf: true, emitEvent: false});
        //   this.netUnitPrice.disable({onlySelf: true, emitEvent: false});
        //   this.grossTotalPrice.disable({onlySelf: true, emitEvent: false});
        //   this.grossUnitPrice.disable({onlySelf: true, emitEvent: false});
        // }

        this.relatedProducts = product.outgoingRelatedProducts.map(itm => ({
          ...itm,
          enabled: itm.necessarily
        }));

        // init position predefined data
        this.updatePosition(null, 'resolveRevenueGL');
      });
  }

  public setBundledProducts(product: ProductModel): void {
    this.bundledProducts = [];
    if (product.bundledProducts) {
      this.bundledProducts = product.bundledProducts;
    }
  }

  public resetBundledProducts(): void {
    this.bundledProducts = [];
    this.activeTab = 'OVERVIEW';
  }

  private getProducts(): void {
    // this.productsService.getAvailableProducts(this.data.invoice.invoiceId, 'outgoing-invoices')
    //   .pipe(takeUntil(this._destroy))
    //   .subscribe((products: ProductModel[]) => {
    //     this.productsList = this.prepareProductsList(products);
    //     this.filterProducts();
    //     this.showDropdownSpin$.next(false);
    //   });

    const activeProductsRequest$ = this.productsService
      .getActiveProducts(undefined, undefined, true, this.data.invoice.salesPriceList)
      .pipe(tap((products: ProductModel[]) => this.productsListAllTypes = this.prepareProductsList(products)));

    const availableProductsRequest$ = this.isPrepaymentInvoice
      ? of(true)
      : this.invoiceApiService
        .getAvailablePositionsFromSO(this.data.invoice.invoiceId, this.companyProfile.workspaceId)
        .pipe(tap((products: ProductModel[]) => this.productsList = [...this.productsList, ...this.prepareProductsList(products)]));

    const availablePrepaymentPositionsRequest$ = this.linkedSoId && !this.isFinalInvoice
      ? this.invoiceApiService
        .getAvailablePrepaymentPositions(this.linkedSoId, this.companyProfile.workspaceId, this.data.invoice.type)
        .pipe(tap((positions: any[]) => {
          positions.map(pos => {
            this.productsList.push({
              ...pos,
              searchLabel: pos.name
            });
          });
          this.productsList = [...this.productsList];
        }))
        : of(true);

      forkJoin([
        activeProductsRequest$,
        availableProductsRequest$,
        availablePrepaymentPositionsRequest$
      ])
        .pipe(takeUntil(this._destroy))
        .subscribe(() => this.showDropdownSpin$.next(false));
  }

  // private getGeneralProductsListDropdown(): void {
  //   this.productsService.getGeneralProductsListDropdown(false)
  //     .pipe(takeUntil(this.destroy$))
  //     .subscribe((response) => {
  //       const filteredGeneralProducts = response
  //         .filter(glProduct => glProduct.entityForm === 'services')
  //         .filter(glProduct => glProduct.isVisibleForList);
  //       this.generalProductsList$.next(filteredGeneralProducts);
  //     });
  // }

  public prepareProductsList(products): ProductModel[] {
    return products.map(item => ({
      ...item,
      searchLabel: getProductSearchValue(item, this.translateService)
    }));
  }

  public createProduct(productType: ProductTypes): void {
    const dialog = this.dialog.open(CompositeProductModalComponent, {
      data: {
        family: null,
        productType: productType
      },
      disableClose: true,
    });

    dialog.afterClosed().subscribe((product: ProductModel) => {
      if (product) {
        this.productsList.push(product);
        this.productsListAllTypes.push(product);
        this.selectedProduct = product;
        this.selectedProductType = product.productType;
        this.selectPositionChanges(product);
        this.setBundledProducts(product);
      }
    });
  }

  public addPosition(): void {
    if (this.addPositionRequest$.getValue()) { return; }
    this.addPositionRequest$.next(true);

    if (this.isTimeTrackingProducts && !this.selectedRecord) {
      this.selectedRecordInvalid = true;
      this.addPositionRequest$.next(false);
      return;
    }

    if (this.positionMediaFormComponentRef) {
      if (this.positionMediaFormComponentRef.mediaType === PositionMediaTypeEnum.TEXT) {
        this.addMediaPosition(PositionMediaTypeEnum.TEXT, {description: this.positionMediaFormComponentRef.textValue});
        return;
      }
      if (this.positionMediaFormComponentRef.mediaType === PositionMediaTypeEnum.IMAGE) {
        this.addMediaPosition(
          PositionMediaTypeEnum.IMAGE,
          {
            file: this.positionMediaFormComponentRef.images && this.positionMediaFormComponentRef.images[0],
            height: this.positionMediaFormComponentRef.heightControlValue as any,
            width: this.positionMediaFormComponentRef.widthControlValue as any,
            alignment: this.positionMediaFormComponentRef.alignmentControlValue as any,
            useAspectRatio: this.positionMediaFormComponentRef.useAspectRatioControlValue as any,
          }
        );
        return;
      }
    }

    this.addProductPosition();
  }

  public addMediaPosition(
    mediaType: PositionMediaTypeEnum,
    data: {
      file?: File,
      description?: string,
      width?: number,
      height?: number,
      alignment?: PdfSettingsAlignEnum,
      useAspectRatio?: boolean,
    }
  ): void {
    data['groupId'] = this.data.groupId;
    if ((this.selectedProduct as any).orderPositionId) {
      data['sourcePositionId'] = (this.selectedProduct as any).orderPositionId;
    }
    if ((this.selectedProduct as any).fileKey) {
      data['fileKey'] = (this.selectedProduct as any).fileKey;
    }
    if ((this.selectedProduct as any).fileName) {
      data['fileName'] = (this.selectedProduct as any).fileName;
    }
    this.invoiceApiService.createPositionMedia(this.data.invoice.invoiceId, mediaType, data)
      .pipe(finalize(() => this.addPositionRequest$.next(false)))
      .subscribe((response: OutgoingInvoicePositionModel) => this.dialogRef.close(response));
  }

  public addProductPosition(): void {
    let description: string = null;

    if (this.isArbitraryProductType || this.isCreditProductType) {
      if (!this.positionName) {
        this.productNameInvalid = true;
        this.addPositionRequest$.next(false);
        return;
      }
    } else {
      if (!this.selectedProduct) {
        this.selectedProductInvalid = true;
        this.addPositionRequest$.next(false);
        return;
      }
    }

    const selectedNotArbitrary = this.selectedProduct &&
      !this.isArbitraryProductType &&
      !this.isCreditProductType;

    if (get(this.selectedProduct, 'logistics')) {
      description = this.selectedProduct.description;
    }

    let position: OutgoingInvoicePositionModel = {
      ...this.form.getRawValue(),
      positionId: this.selectedProduct.id,
      productTypeForGrouping: this.selectedProductType,
      positionType: this.isPrepaymentProductType
        ? 'PREPAYMENT'
        : null,
      description: selectedNotArbitrary
        ? this.selectedProduct.name
        : this.positionName,
      manufacturerCode: get(this.selectedProduct, 'manufacturer_code') || get(this.selectedProduct, 'manufacturerCode'),
      productId: selectedNotArbitrary ? +this.selectedProduct.id : null,
      generalProductId: selectedNotArbitrary ? this.selectedProduct.generalProductId : null,
      runpleId: selectedNotArbitrary
        ? (this.selectedProduct.runpleId || this.selectedProduct.runple_id)
        : null,
      type: this.selectedProductType,
      bundleType: this.selectedProduct.bundleType || this.selectedProduct.bundle_type || null,
      sourcePositionId: this.selectedProduct['orderPositionId'],
      arbitraryProductType: this.selectedProduct['arbitraryProductType'],
      sourceInvoiceId: this.selectedProduct['sourceInvoiceId'],
      notFromBaseDocument: this.notFromBaseDocument,
      logistics: (this.selectedProduct && !this.isArbitraryProductType)
        ? !!this.selectedProduct.logistics
        : false,
      productDescription: !this.isServiceProductType() && !this.isGeneralServiceType && !this.isArbitraryProductType
        ? this.productDescription.value
        : description,
      providedServices: this.isServiceProductType() || this.isGeneralServiceType || this.isArbitraryProductType
        ? this.providedServices.value
        : null,
      documentId: this.data.invoice.invoiceId,
      correctionType: this.correctionType.value,
      productRelatedIds: this.relatedProducts
        ? this.relatedProducts
          .filter(itm => itm.enabled)
          .map(itm => itm.id)
        : [],
      groupId: this.data.groupId,
      accountingSettings: {
        ...this.accountingSettings,
        ...(this.positionAccountingSettingsRef && this.positionAccountingSettingsRef.getFormValue()),
      },
    };

    if (this.isArbitraryProductType) {
      position.description = this.positionName;
    }

    if (this.isTimeTrackingProducts) {
      position = {
        ...this.positionFromTimeTracking,
        ...this.form.getRawValue(),
        recordId: this.selectedRecord.id,
      };
    }

    this.invoiceApiService.createPositionByPredefinedForm(this.data.invoice.invoiceId, position)
      .pipe(finalize(() => this.addPositionRequest$.next(false)))
      .subscribe((response: OutgoingInvoicePositionModel) => this.dialogRef.close(response));
  }

  public displayUserDefinedPosition(): void {
    this.positionMediaFormComponentRef.setMediaType(PositionMediaTypeEnum.REGULAR);
    this.selectedProductType = ProductTypes.ARBITRARY;
    this.selectedProduct = { name: this.translateService.instant('POSITIONS.USER_DEFINED_POSITION') };
    this.positionName = null;
    this.accountingSettings = null;
    if (this.positionAccountingSettingsRef) {
      this.positionAccountingSettingsRef.resetForm();
    }
    this.initForm();
    this.productTypeControl.setValue(ProductTypes.GOODS);
    this.getProductUnits();
    if (this.vatBlocked) {
      this.vat.setValue(0);
      this.vat.disable({onlySelf: true, emitEvent: false});
      if (this.priceOptionNet) {
        this.grossUnitPrice.disable({onlySelf: true, emitEvent: false});
        this.grossTotalPrice.disable({onlySelf: true, emitEvent: false});
      }
    }
    this.updatePosition(null, 'resolveRevenueGL');
  }

  public displayCreditPosition(): void {
    this.positionMediaFormComponentRef.setMediaType(PositionMediaTypeEnum.REGULAR);
    this.selectedProductType = ProductTypes.CREDIT;
    this.selectedProduct = { name: this.translateService.instant('Credit position') };
    this.positionName = null;

    this.initForm();
    this.productTypeControl.setValue(ProductTypes.CREDIT);
    if (this.vatBlocked) {
      this.vatTotal.disable({onlySelf: true, emitEvent: false});
      this.vat.setValue(0);
    }
  }

  public displayCorrectionPosition(): void {
    this.selectedProductType = ProductTypes.CORRECTION;
    this.selectedProduct = { name: this.translateService.instant('FORM.CORRECTIONS') };
    this.positionName = null;
    this.initForm();
    this.correctionType.setValue('NEGATIVE');
    this.productTypeControl.setValue(ProductTypes.CORRECTION);
    this.specifyAmountsSeparately.setValue(false);
    this.correctionType.disable({ onlySelf: true, emitEvent: false });
    if (this.vatBlocked) {
      this.vatTotal.disable({onlySelf: true, emitEvent: false});
      this.vat.setValue(0);
    }
    // init position predefined data
    this.updatePosition(null, 'resolveRevenueGL');
  }

  public displayTextPosition(): void {
    this.positionMediaFormComponentRef.setMediaType(PositionMediaTypeEnum.TEXT);
    this.selectedProduct = {
      name: this.translateService.instant('ECOMMERCE.TEXT'),
      mediaType: PositionMediaTypeEnum.TEXT,
      icon: 'user-defined'
    } as any;
  }

  public displayImagePosition(): void {
    this.positionMediaFormComponentRef.setMediaType(PositionMediaTypeEnum.IMAGE);
    this.selectedProduct = {
      name: this.translateService.instant('FORM.IMAGE'),
      mediaType: PositionMediaTypeEnum.IMAGE,
      icon: 'image'
    } as any;
  }

  public updatePosition(value, fieldName, force: boolean = false): void {
    let description: string = null;
    this.addPositionRequest$.next(true);

    switch (fieldName) {
      case 'delivered':
      case 'providedFrom':
      case 'providedTo':
        value = this.datePipe.transform(value, 'yyyy-MM-dd');
        break;
    }

    if (get(this.selectedProduct, 'logistics')) {
      description = this.selectedProduct.description;
    }

    if (fieldName === 'specifyAmountsSeparately' && !value && !force) {
      if (
        (this.netTotalPrice.value && (this.netTotalPrice.value !== '0.00')) ||
        (this.grossTotalPrice.value && (this.grossTotalPrice.value !== '0.00')) ||
        (this.vatTotal.value && (this.vatTotal.value !== 0))
      ) {
        const dialog = this.dialog.open(WarningModalComponent, {
          data: CHANGE_CORRECTION_CALCULATION_MODAL_DATA,
          disableClose: true,
        });

        dialog.afterClosed().subscribe(res => {
          if (res === CommonModalsActionsEnum.CONFIRM) {
            this.updatePosition(value, fieldName, true);
          } else {
            this.specifyAmountsSeparately.setValue(true);
          }
        });

        return;
      }
    }


    this.recalculatePositionApiService.recalculatePosition(DocumentTypesUppercaseEnum.OIN, {
      protoPosition: {
        ...this.form.getRawValue(),
        salesPriceListName: this.data.invoice.salesPriceList,
        productId: this.selectedProduct.id,
        sourceInvoiceId: this.selectedProduct['sourceInvoiceId'],
        sourcePositionId: this.selectedProduct['orderPositionId'],
        productTypeForGrouping: this.selectedProductType,
        productType: this.productTypeControl.value,
        arbitraryPositionProductType: this.productTypeControl.value,
        positionType: this.isPrepaymentProductType
          ? 'PREPAYMENT'
          : null,
        productDescription: !this.isServiceProductType() && !this.isGeneralServiceType && !this.isArbitraryProductType
          ? this.productDescription.value
          : description,
        providedServices: this.isServiceProductType() || this.isGeneralServiceType || this.isArbitraryProductType
          ? this.providedServices.value
          : null,
        description: this.isCreditProductType || this.isPrepaymentProductType || this.isArbitraryProductType
          ? this.form.get('description').value
          : null,
        notFromBaseDocument: this.notFromBaseDocument,
        logistics: this.selectedProduct && !this.isArbitraryProductType
          ? !!this.selectedProduct.logistics
          : false,
        timeTracking: this.isTimeTrackingProducts,
        correctionType: this.isCorrectionProductType ? this.correctionType.value : null,
        quantity: this.isPrepaymentProductType
          ? 1
          : this.quantity.value,
        priceFromBundledProducts: get(this.selectedProduct, 'price_from_bundled_products')
          || get(this.selectedProduct, 'prices.price.priceFromBundledProducts'),
        bundleType: get(this.selectedProduct, 'bundle_type')
          || get(this.selectedProduct, 'bundleType'),
        accountingSettings: {
          ...this.accountingSettings,
          ...(this.positionAccountingSettingsRef && this.positionAccountingSettingsRef.getFormValue()),
          productTypeId: get(this.accountingSettings, 'productTypeId') || get(this.accountingSettings, 'glProductTypeId')
        },
      },
      fieldUpdateRequest: {
        fieldName: fieldName,
        fieldValue: value
      },
      type: this.selectedProductType,
      documentId: this.data.invoice.invoiceId,
    })
      .pipe(finalize(() => this.addPositionRequest$.next(false)))
      .subscribe(product => {
        this.form.patchValue(product);
        this.previousUnitType = product.unitType;
        this.previousUpdatedPosition = product;
        if (product.accountingSettings) {
          this.accountingSettings = product.accountingSettings;
        }
      }, (error) => {
        if (error && error.error && error.error.errorCodes) {
          if (error.error.errorCodes.length &&  error.error.errorCodes.includes(1786)) {
            if (this.previousUpdatedPosition) {
              this.grossTotalPrice.patchValue(this.previousUpdatedPosition.grossTotalPrice);
              this.grossUnitPrice.patchValue(this.previousUpdatedPosition.grossUnitPrice);
              this.netTotalPrice.patchValue(this.previousUpdatedPosition.netTotalPrice);
              this.netUnitPrice.patchValue(this.previousUpdatedPosition.netUnitPrice);
            }
          }
        }
        if (fieldName === 'unitType') {
          // set previous value of unit type in case of error
          this.unitType.patchValue(this.previousUnitType);
          // redraw quantity control view
          setTimeout(() => this.quantity.patchValue(this.quantity.value), 10);
        }
      });
  }

  public setCurrencyValue(value): number {
    if (!value) { return value; }
    return +(value / 1e2).toFixed(2);
  }

  // public updateDescription(description: string): void {
  //   this.selectedProduct = {
  //     ...this.selectedProduct,
  //     displayDescription: description
  //   };
  // }

  public updateDescriptionByProductType(): void {
    if (
      this.selectedProductType === ProductTypes.SERVICES
      || this.isArbitraryProductType
      || this.isGeneralServiceType
    ) {
      this.updatePosition(this.providedServices.value, 'providedServices');
    } else {
      this.updatePosition(this.productDescription.value, 'productDescription');
    }
  }

  getPlainText(html: string): string {
    if (!html) { return ''; }

    const normalized = html
      .replace(/<\/(div|p|br)>/gi, ' ')
      .replace(/<(div|p|br)[^>]*>/gi, ' ');

    const doc = new DOMParser().parseFromString(normalized, 'text/html');

    return doc.body.textContent.replace(/\s+/g, ' ').trim();
  }

  public isServiceProductType(): boolean {
    return this.selectedProductType === ProductTypes.SERVICES;
  }
  get isGoodsProductType(): boolean {
    return this.selectedProductType === ProductTypes.GOODS;
  }
  get isDigitalProductType(): boolean {
    return this.selectedProductType === ProductTypes.DIGITAL;
  }
  get isCreditProductType(): boolean {
    return this.selectedProductType === ProductTypes.CREDIT;
  }
  get isCorrectionProductType(): boolean {
    return this.selectedProductType === ProductTypes.CORRECTION;
  }
  get isTemplate(): boolean {
    return this.data && this.data.invoice.status === OutgoingInvoiceListTabsEnum.TEMPLATE;
  }
  get isGeneralProductType(): boolean {
    return this.selectedProductType === ProductTypes.GENERAL;
  }
  get isPrepaymentProductType(): boolean {
    return this.selectedProductType === ProductTypes.PREPAYMENT
      || this.selectedProductType === ProductTypes.PREPAYMENT.toUpperCase();
  }
  get isGeneralServiceType(): boolean {
    if (!this.selectedProduct) { return false; }
    return this.selectedProduct.entityForm === ProductTypes.SERVICES;
  }
  get isGeneralGoodsType(): boolean {
    if (!this.selectedProduct) { return false; }
    return this.selectedProduct.entityForm === ProductTypes.GOODS;
  }
  get isGeneralDigitalType(): boolean {
    if (!this.selectedProduct) { return false; }
    return this.selectedProduct.entityForm === ProductTypes.DIGITAL;
  }
  get isBundleGoods(): boolean {
    if (!this.selectedProduct) { return false; }
    return this.selectedProduct.type === ProductTypes.BUNDLE
      && (
        this.selectedProduct['bundle_type'] === ProductTypes.GOODS
        || this.selectedProduct['bundleType'] === ProductTypes.GOODS
      );
  }
  get isBundleServices(): boolean {
    if (!this.selectedProduct) { return false; }
    return this.selectedProduct.type === ProductTypes.BUNDLE
      && (
        this.selectedProduct['bundle_type'] === ProductTypes.SERVICES
        || this.selectedProduct['bundleType'] === ProductTypes.SERVICES
      );
  }
  get isBundleDigital(): boolean {
    if (!this.selectedProduct) { return false; }
    return this.selectedProduct.type === ProductTypes.BUNDLE
      && (
        this.selectedProduct['bundle_type'] === ProductTypes.DIGITAL
        || this.selectedProduct['bundleType'] === ProductTypes.DIGITAL
      );
  }
  get isArbitraryProductType(): boolean {
    return this.selectedProductType === ProductTypes.ARBITRARY;
  }

  get allowFractionalValues(): boolean {
    if (!this.unitType.value || !this.unitsListSource.length) { return true; }
    const selectedUnit = this.unitsListSource
      .find((itm: ProductUnitModel) => itm.name === this.unitType.value);
    const hasFractionalAvailableQuantity = (
        (this.hasLinkedSO || this.hasLinkedOFR || this.hasLinkedSBC)
        && !this.notFromBaseDocument
        && this.selectedProduct
      )
        ? !!(this.selectedProduct.available % 1)
        : false;
    return (selectedUnit && !hasFractionalAvailableQuantity)
      ? selectedUnit.allowFractionalValues
      : hasFractionalAvailableQuantity;
  }

  get linkedSoId(): number {
    const linkedSoList = get(this, 'data.invoice.linkedDocuments', [])
      .filter(doc => doc.printableType === DocumentTypesUppercaseEnum.SO || doc.printableType === DocumentTypesUppercaseEnum.OFR);
    return linkedSoList && linkedSoList.length
      ? linkedSoList[0].documentId
      : null;
  }

  get isPrepaymentInvoice(): boolean {
    return get(this.data, 'invoice.type') === OinTypeEnum.PREPAYMENT;
  }

  get isRegularInvoice(): boolean {
    return get(this.data, 'invoice.type') === OinTypeEnum.REGULAR;
  }

  get isFinalInvoice(): boolean {
    return get(this.data, 'invoice.type') === OinTypeEnum.FINAL;
  }

  get priceOptionNet(): boolean {
    return this.data.invoice.preferences.priceOption === PriceOptionEnum.NET || this.data.invoice.billingData.smallBusiness;
  }
  get priceOptionGross(): boolean {
    return this.data.invoice.preferences.priceOption === PriceOptionEnum.GROSS && !this.data.invoice.billingData.smallBusiness;
  }
  get mediaType(): PositionMediaTypeEnum {
    if (this.positionMediaFormComponentRef && this.positionMediaFormComponentRef.mediaType) {
      return this.positionMediaFormComponentRef.mediaType;
    }
    return PositionMediaTypeEnum.REGULAR;
  }

  get isGeneralPartner(): boolean {
    return this.data.invoice && this.data.invoice.partnerType === CustomerTypeEnum.GENERAL;
  }
  get hasLinkedSO(): boolean {
    return !!get(this.data.invoice, 'linkedDocuments', [])
      .some(doc => doc.printableType === DocumentTypesUppercaseEnum.SO);
  }
  get hasLinkedOFR(): boolean {
    return !!get(this.data.invoice, 'linkedDocuments', [])
      .filter(doc => doc.printableType === DocumentTypesUppercaseEnum.OFR).length;
  }
  get hasLinkedECO(): boolean {
    return !!get(this.data.invoice, 'linkedDocuments', [])
      .filter(doc => doc.printableType === DocumentTypesUppercaseEnum.ECO).length;
  }
  get hasLinkedSBC(): boolean {
    return !!get(this.data.invoice, 'linkedDocuments', [])
      .filter(doc => doc.printableType === DocumentTypesUppercaseEnum.SBC).length;
  }
  get hasLinkedNotTechnicalSO(): boolean {
    return !!get(this.data.invoice, 'linkedDocuments', [])
      .filter(doc => doc.printableType === DocumentTypesUppercaseEnum.SO && doc.type === DocumentTypesUppercaseEnum.SO)
      .length;
  }
  get hasLinkedDocument(): boolean {
    return this.hasLinkedSBC || this.hasLinkedSO;
  }
  get vatBlocked(): boolean {
    return this.data.invoice.billingData.vatDisabled || this.data.invoice.billingData.smallBusiness;
  }

  get grossTotalPrice(): FormControl { return this.form.get('grossTotalPrice') as FormControl; }
  get grossUnitPrice(): FormControl { return this.form.get('grossUnitPrice') as FormControl; }
  get addRemark(): FormControl { return this.form.get('addRemark') as FormControl; }
  get netTotalPrice(): FormControl { return this.form.get('netTotalPrice') as FormControl; }
  get providedFrom(): FormControl { return this.form.get('providedFrom') as FormControl; }
  get netUnitPrice(): FormControl { return this.form.get('netUnitPrice') as FormControl; }
  get productDescription(): FormControl { return this.form.get('productDescription') as FormControl; }
  get providedServices(): FormControl { return this.form.get('providedServices') as FormControl; }
  get providedTo(): FormControl { return this.form.get('providedTo') as FormControl; }
  get delivered(): FormControl { return this.form.get('delivered') as FormControl; }
  get quantity(): FormControl { return this.form.get('quantity') as FormControl; }
  get remark(): FormControl { return this.form.get('remark') as FormControl; }
  get discount(): FormControl { return this.form.get('discount') as FormControl; }
  get discountAmountNet(): FormControl { return this.form.get('discountAmountNet') as FormControl; }
  get discountAmountGross(): FormControl { return this.form.get('discountAmountGross') as FormControl; }
  get addDiscount(): FormControl { return this.form.get('addDiscount') as FormControl; }
  get vat(): FormControl { return this.form.get('vat') as FormControl; }
  get productTypeControl(): FormControl { return this.form.get('productType') as FormControl; }
  get unitType(): FormControl { return this.form.get('unitType') as FormControl; }
  get correctionType(): FormControl { return this.form.get('correctionType') as FormControl; }
  get specifyAmountsSeparately(): FormControl { return this.form.get('specifyAmountsSeparately') as FormControl; }
  get vatTotal(): FormControl { return this.form.get('vatTotal') as FormControl; }

}
